import React from "react";
import { WeekDay } from "@/types/amenity";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { TimePicker } from "@/components/common/TimePicker";
import { PlusCircle, Trash2 } from "lucide-react";
import { Label } from "@/components/ui/label";

export type WeekScheduleItem = {
  days: WeekDay[];
  startTime: string;
  endTime: string;
};

type Props = {
  label?: string;
  value: WeekScheduleItem[];
  onChange: (next: WeekScheduleItem[]) => void;
  weekDays?: WeekDay[];
  addButtonText?: string;
  className?: string;
  itemClassName?: string;
  errors?: { startTime?: string; endTime?: string; days?: string }[];
  disabled?: boolean;
};

const WeekSchedulePicker: React.FC<Props> = ({
  label = "Work schedule",
  value,
  onChange,
  weekDays,
  addButtonText,
  className,
  itemClassName,
  errors,
  disabled = false,
}) => {
  const days = weekDays || (Object.values(WeekDay) as WeekDay[]);

  const updateItem = (i: number, patch: Partial<WeekScheduleItem>) => {
    const next = value.map((v, idx) => (idx === i ? { ...v, ...patch } : v));
    onChange(next);
  };

  const addItem = () => {
    onChange([...value, { days: [], startTime: "", endTime: "" }]);
  };

  const removeItem = (i: number) => {
    const next = value.filter((_, idx) => idx !== i);
    onChange(next);
  };

  const toggleDay = (i: number, day: WeekDay, checked: boolean) => {
    const current = value[i]?.days || [];
    const nextDays = checked
      ? [...current, day]
      : current.filter((d) => d !== day);
    updateItem(i, { days: nextDays });
  };

  return (
    <div className={className}>
      <div className="mb-4 flex flex-row items-center justify-between">
        <h3 className="text-2xl text-black">{label}</h3>
        <Button
          type="button"
          variant="new_outline"
          onClick={addItem}
          disabled={disabled}
        >
          <PlusCircle />
          {addButtonText ? <span>{addButtonText}</span> : null}
        </Button>
      </div>
      <div className="space-y-4">
        {value.map((item, index) => {
          const err = errors?.[index];
          return (
            <div key={index} className={`space-y-3 ${itemClassName || ""}`}>
              <div className="flex gap-4">
                <TimePicker
                  id={`schedules.${index}.startTime` as const}
                  label="FROM"
                  required
                  register={(/* name */) => ({}) as any}
                  error={err?.startTime}
                  watch={() => item.startTime as any}
                  setValue={(/* name, val */) => {}}
                  max={item.endTime}
                  disabled={disabled}
                  value={item.startTime}
                  onChange={(e: any) =>
                    updateItem(index, { startTime: e?.target?.value || e })
                  }
                />
                <TimePicker
                  id={`schedules.${index}.endTime` as const}
                  label="TO"
                  required
                  register={() => ({}) as any}
                  error={err?.endTime}
                  watch={() => item.endTime as any}
                  setValue={() => {}}
                  min={item.startTime}
                  disabled={disabled}
                  value={item.endTime}
                  onChange={(e: any) =>
                    updateItem(index, { endTime: e?.target?.value || e })
                  }
                />
              </div>
              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-wrap gap-4">
                  {days.map((day) => (
                    <Label
                      key={`${index}-${day}`}
                      className="flex items-center gap-2 font-normal"
                    >
                      <Checkbox
                        checked={item.days.includes(day)}
                        disabled={disabled}
                        onCheckedChange={(checked: boolean) =>
                          toggleDay(index, day, checked)
                        }
                      />
                      <span>{day}</span>
                    </Label>
                  ))}
                </div>
                {value.length > 1 && (
                  <Button
                    type="button"
                    variant="new_ghost"
                    size="icon_sm"
                    onClick={() => removeItem(index)}
                    disabled={disabled}
                    aria-label="Remove schedule"
                  >
                    <Trash2 className="text-custom-destructive-dark" />
                  </Button>
                )}
              </div>
              {err?.days && (
                <p className="text-destructive mt-1 text-sm">{err.days}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeekSchedulePicker;
